import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Button,
  Alert,
  Box,
  IconButton,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  FormControl,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  isUserValid,
  pb,
  getCurrentUser,
  hasAdminPermission,
} from "../services/pocketbase";
import TournamentEditor from "../components/TournamentEditor";
import {
  Edit as EditIcon,
  Add as AddIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { CalendarToday, People } from "@mui/icons-material"; // Add this import at the top
import TournamentParticipants from "../components/TournamentParticipants";
import { Link } from "react-router-dom"; // Add this import

const Home = () => {
  const [tournaments, setTournaments] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [user, setUser] = useState(getCurrentUser());
  const isAdmin = hasAdminPermission(user);
  const [registrations, setRegistrations] = useState({});
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGame, setSelectedGame] = useState("all");
  const [filteredTournaments, setFilteredTournaments] = useState([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [tournamentToCancel, setTournamentToCancel] = useState(null);
  const isLoggedIn = isUserValid(); // Add this line to check login status
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState(null);

  const openCancelDialog = (tournament) => {
    setTournamentToCancel(tournament);
    setCancelDialogOpen(true);
  };

  const handleCancelRegistration = async () => {
    try {
      if (!user || !tournamentToCancel) {
        setError("Missing user or tournament information");
        setCancelDialogOpen(false);
        return;
      }

      console.log(
        `Searching for registration: user=${user.id}, tournament=${tournamentToCancel.id}`
      );

      // Find the registration to cancel - use a more direct approach
      try {
        const existingRegistrations = await pb
          .collection("tournament_registrations")
          .getFullList({
            filter: `user="${user.id}" && tournament="${tournamentToCancel.id}"`,
          });

        if (existingRegistrations.length === 0) {
          console.error("No registration found with the specified criteria");
          setError("Registration not found");
          setCancelDialogOpen(false);
          return;
        }

        console.log(`Found registration: ${existingRegistrations[0].id}`);

        // Delete the registration
        await pb
          .collection("tournament_registrations")
          .delete(existingRegistrations[0].id);

        // Update local state
        setRegistrations((prev) => {
          const updated = { ...prev };
          delete updated[tournamentToCancel.id];
          return updated;
        });

        // Refresh tournaments
        await fetchTournaments();
        setCancelDialogOpen(false);
      } catch (fetchErr) {
        console.error("Error fetching registration:", fetchErr);
        setError(`Error finding registration: ${fetchErr.message}`);
        setCancelDialogOpen(false);
      }
    } catch (err) {
      console.error("Error canceling registration:", err);
      setError(`Failed to cancel registration: ${err.message}`);
      setCancelDialogOpen(false);
    }
  };

  // Update your useEffect or add a new one to filter tournaments when search or game filter changes
  useEffect(() => {
    if (tournaments.length > 0) {
      const filtered = tournaments.filter((tournament) => {
        // Apply game filter
        if (selectedGame !== "all" && tournament.game !== selectedGame) {
          return false;
        }

        // Apply search filter
        if (
          searchQuery &&
          !tournament.name.toLowerCase().includes(searchQuery.toLowerCase())
        ) {
          return false;
        }

        return true;
      });

      setFilteredTournaments(filtered);
    } else {
      setFilteredTournaments([]);
    }
  }, [tournaments, searchQuery, selectedGame]);

  const handleViewParticipants = async (tournament) => {
    try {
      const registrations = await pb
        .collection("tournament_registrations")
        .getList(1, 50, {
          filter: `tournament="${tournament.id}"`,
          expand: "user",
        });
      setSelectedParticipants(registrations.items);
      setParticipantsOpen(true);
    } catch (err) {
      console.error("Error fetching participants:", err);
      setError("Failed to load participants");
    }
  };

  const handleSignup = async (tournamentId) => {
    try {
      if (!user) {
        setError("Please login to sign up for tournaments");
        return;
      }

      const existingRegistration = await pb
        .collection("tournament_registrations")
        .getList(1, 1, {
          filter: `user="${user.id}" && tournament="${tournamentId}"`,
        });

      if (existingRegistration.items.length > 0) {
        setError("You are already registered for this tournament");
        return;
      }

      // Get the tournament to check max participants
      const tournament = await pb
        .collection("tournaments")
        .getOne(tournamentId, {
          expand: "tournament_registrations(tournament)",
        });

      // Check if tournament is full (skip check if max_participants is 0/unlimited)
      if (
        tournament.max_participants !== 0 &&
        tournament.expand?.["tournament_registrations(tournament)"]?.length >=
          tournament.max_participants
      ) {
        setError("This tournament is already full");
        return;
      }

      await pb.collection("tournament_registrations").create({
        user: user.id,
        tournament: tournamentId,
        registration_date: new Date().toISOString(),
        status: "confirmed",
      });

      setRegistrations((prev) => ({
        ...prev,
        [tournamentId]: true,
      }));

      await fetchTournaments();
    } catch (err) {
      console.error("Error signing up for tournament:", err);
      setError("Failed to sign up for tournament. Please try again.");
    }
  };

  const checkRegistrations = async () => {
    if (!user) return;

    try {
      const userRegistrations = await pb
        .collection("tournament_registrations")
        .getList(1, 50, {
          filter: `user="${user.id}"`,
        });

      const registrationMap = {};
      userRegistrations.items.forEach((reg) => {
        registrationMap[reg.tournament] = true;
      });

      setRegistrations(registrationMap);
    } catch (err) {
      console.error("Error checking registrations:", err);
    }
  };

  // Modify the useEffect to include registration check
  useEffect(() => {
    const initializeData = async () => {
      try {
        await fetchTournaments();
        await checkRegistrations();
      } catch (error) {
        console.error("Error in initialization:", error);
      }
    };

    initializeData();
    // eslint-disable-next-line
  }, [user]);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      setError(null);

      await new Promise((resolve) => setTimeout(resolve, 100));

      if (!pb.authStore.isValid) {
        setLoading(false);
        return;
      }

      const records = await pb.collection("tournaments").getList(1, 50, {
        sort: "start_date",
        expand: "tournament_registrations(tournament)",
        $autoCancel: false,
      });
      // eslint-disable-next-line
      const now = new Date();
      const filteredTournaments = records.items
        .filter((tournament) => {
          const tournamentStartDate = new Date(tournament.start_date);
          const tournamentEndDate = new Date(tournament.end_date);
          const now = new Date();

          // Show tournament if:
          // 1. It hasn't started yet, OR
          // 2. It has started and hasn't ended yet, OR
          // 3. It ends today (compare dates without time)
          const endDateWithoutTime = new Date(
            tournamentEndDate.setHours(0, 0, 0, 0)
          );
          const nowWithoutTime = new Date(now.setHours(0, 0, 0, 0));

          return (
            tournamentStartDate > now ||
            (tournamentStartDate <= now && tournamentEndDate >= now) ||
            endDateWithoutTime.getTime() === nowWithoutTime.getTime()
          );
        })
        .sort((a, b) => {
          const dateA = new Date(a.start_date);
          const dateB = new Date(b.start_date);
          return dateA - dateB;
        });

      setTournaments(filteredTournaments);
    } catch (err) {
      console.error("Error fetching tournaments:", err);
      if (err.status === 403) {
        setError(
          "You do not have permission to view tournaments. Please contact an administrator."
        );
      } else {
        setError("Failed to load tournaments. Please try again later.");
      }
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleAuthChange = () => {
      const currentUser = getCurrentUser();
      setUser(currentUser);
    };

    handleAuthChange();
    pb.authStore.onChange(handleAuthChange);
    return () => pb.authStore.onChange(null);
  }, []);

  // Combined useEffect for test data and tournaments
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Fetch tournaments
        await fetchTournaments();
      } catch (error) {
        console.error("Error in initialization:", error);
      }
    };

    initializeData();
  }, []); // Run once when component mounts

  const handleEditorClose = (refresh) => {
    setEditorOpen(false);
    setSelectedTournament(null);
    if (refresh) {
      fetchTournaments();
    }
  };
  // eslint-disable-next-line
  const handleEditTournament = (tournament) => {
    setSelectedTournament(tournament);
    setEditorOpen(true);
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading events...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // Add this block to check if user is logged in
  if (!isLoggedIn) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            borderRadius: 2,
          }}
        >
          <Alert
            severity="info"
            sx={{
              mb: 3,
              width: "80%",
              maxWidth: "500px",
              "& .MuiAlert-message": { fontSize: "1.1rem" },
            }}
          >
            Please login to view events
          </Alert>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            sx={{
              backgroundColor: "#c4d4e0",
              "&:hover": {
                backgroundColor: "#848d94",
              },
            }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  // Function to send webhook notification for event deletion
  const sendDeleteWebhookNotification = async (tournament) => {
    try {
      const currentUser = pb.authStore.model;
      const timestamp = new Date().toISOString();

      // Prepare webhook data
      const webhookData = {
        content: "Event Deleted! ❌",
        embeds: [
          {
            title: "Event Deleted",
            description: `**Event deleted by ${currentUser.username}**`,
            color: 0xff0000, // Red color for deletion
            fields: [
              {
                name: "Event Name",
                value: tournament.name,
                inline: true,
              },
              {
                name: "Game",
                value: getGameDisplayName(tournament.game),
                inline: true,
              },
              {
                name: "Start Date",
                value: new Date(tournament.start_date).toLocaleString(),
                inline: true,
              },
              {
                name: "End Date",
                value: new Date(tournament.end_date).toLocaleString(),
                inline: true,
              },
              {
                name: "Status",
                value:
                  tournament.status.charAt(0).toUpperCase() +
                  tournament.status.slice(1),
                inline: true,
              },
            ],
            timestamp: timestamp,
            footer: {
              text: `TOG Events | Deleted by ${currentUser.username}`,
            },
          },
        ],
      };

      // Send to Discord webhook
      const response = await fetch("/api/webhooks/event-logs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });

      if (!response.ok) {
        console.error("Failed to send webhook notification");
      }
    } catch (err) {
      console.error("Error sending webhook notification:", err);
    }
  };

  // Helper function to get readable game names (same as in TournamentEditor)
  const getGameDisplayName = (gameId) => {
    const games = {
      blackops6: "Black Ops 6",
      gta5: "GTA 5",
      other: "Other Event",
    };
    return games[gameId] || gameId;
  };

  // Function to open delete confirmation dialog
  const openDeleteDialog = (tournament) => {
    setTournamentToDelete(tournament);
    setDeleteDialogOpen(true);
  };

  // Function to handle tournament deletion
  const handleDeleteTournament = async () => {
    try {
      if (!tournamentToDelete) {
        setError("No tournament selected for deletion");
        setDeleteDialogOpen(false);
        return;
      }

      // Send webhook notification before deleting
      await sendDeleteWebhookNotification(tournamentToDelete);

      // Delete the tournament
      await pb.collection("tournaments").delete(tournamentToDelete.id);

      // Update local state
      setTournaments(tournaments.filter((t) => t.id !== tournamentToDelete.id));
      setFilteredTournaments(
        filteredTournaments.filter((t) => t.id !== tournamentToDelete.id)
      );

      setDeleteDialogOpen(false);
    } catch (err) {
      console.error("Error deleting tournament:", err);
      setError(`Failed to delete event: ${err.message}`);
      setDeleteDialogOpen(false);
    }
  };

  // Modify the tournament card render section
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4">Events</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedTournament(null);
              setEditorOpen(true);
            }}
            sx={{
              backgroundColor: "#c4d4e0",
              "&:hover": {
                backgroundColor: "#848d94",
              },
            }}
          >
            Create Event
          </Button>
        )}
      </Box>

      {/* Add search and filter controls */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 4,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <FormControl sx={{ minWidth: 150 }}>
          <Select
            value={selectedGame}
            onChange={(e) => setSelectedGame(e.target.value)}
            displayEmpty
            variant="outlined"
            sx={{
              height: "56px",
            }}
          >
            <MenuItem value="all">All Games</MenuItem>
            <MenuItem value="blackops6">Black Ops 6</MenuItem>
            <MenuItem value="gta5">GTA 5</MenuItem>
            <MenuItem value="other">Other Events</MenuItem>
          </Select>
        </FormControl>

        <TextField
          placeholder="Search by Name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { height: "56px" },
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Typography>Loading events...</Typography>
      ) : filteredTournaments.length === 0 ? (
        <Typography>No events found.</Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredTournaments.map((tournament) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={tournament.id}
              sx={{
                width: {
                  xs: "100%", // Full width on mobile
                  md: "31.9%", // 30% width on desktop
                },
              }}
            >
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  backgroundColor: "rgb(40, 44, 52)",
                  overflow: "visible",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    paddingTop: "56.25%", // 16:9 aspect ratio
                    backgroundColor: "rgb(40, 44, 52)",
                  }}
                >
                  {tournament.thumbnail && (
                    <Box
                      component="img"
                      src={`${pb.baseUrl}/api/files/tournaments/${tournament.id}/${tournament.thumbnail}`}
                      alt={tournament.name}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  {isAdmin && (
                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedTournament(tournament);
                          setEditorOpen(true);
                        }}
                        sx={{ color: "primary.main" }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleViewParticipants(tournament)}
                        sx={{ color: "info.main" }}
                      >
                        <PeopleIcon />
                      </IconButton>
                      {/* Add Delete Button */}
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(tournament)}
                        sx={{ color: "error.main" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                <CardContent
                  sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
                >
                  <Typography gutterBottom variant="h5" component="h2">
                    {tournament.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      wordWrap: "break-word",
                      whiteSpace: "pre-wrap", // This will preserve line breaks
                    }}
                  >
                    {tournament.description}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <CalendarToday sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body2">
                      {new Date(tournament.start_date).toLocaleDateString()} -{" "}
                      {new Date(tournament.end_date).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <People sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body2">
                      {tournament.expand?.[
                        "tournament_registrations(tournament)"
                      ]?.length || 0}
                      {tournament.max_participants === 0
                        ? " Participants (Unlimited)"
                        : `/${tournament.max_participants} Participants`}
                    </Typography>
                  </Box>
                  {isUserValid() && !registrations[tournament.id] && (
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => handleSignup(tournament.id)}
                    >
                      SIGN UP
                    </Button>
                  )}
                  {registrations[tournament.id] && (
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => openCancelDialog(tournament)}
                    >
                      Cancel Registration
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <TournamentEditor
        open={editorOpen}
        onClose={handleEditorClose}
        tournament={selectedTournament}
      />

      <TournamentParticipants
        open={participantsOpen}
        onClose={() => setParticipantsOpen(false)}
        participants={selectedParticipants}
      />

      {/* Registration Cancellation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        sx={{ zIndex: 99 }}
      >
        <DialogTitle>Cancel Registration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel your registration for this event?
            You will lose your spot and may not be able to register again if the
            event fills up.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCancelRegistration}
            color="error"
            variant="contained"
          >
            Delete Registration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the event "
            {tournamentToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTournament} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Home;
